import { ArrowLeft, Calendar, Filter, TrendingUp, Users, CheckCircle, AlertCircle } from 'lucide-react';
import { Loading } from '@/core/ui/components/Loading';
import { useAnnualBilling } from '../hooks';
import { ReportExportButton } from '../components';
import { ChargeType, type BillingCellStatus } from '@yawi/types';

interface AnnualBillingPageProps {
  onBack: () => void;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const STATUS_STYLES: Record<BillingCellStatus, string> = {
  PAID: 'bg-green-100 text-green-800',
  PARTIAL: 'bg-yellow-100 text-yellow-800',
  PENDING: 'bg-slate-100 text-slate-600',
  OVERDUE: 'bg-red-100 text-red-800',
  NO_CHARGE: 'bg-white text-slate-300',
};

const STATUS_LABELS: Record<BillingCellStatus, string> = {
  PAID: 'Pagado',
  PARTIAL: 'Parcial',
  PENDING: 'Pendiente',
  OVERDUE: 'Vencido',
  NO_CHARGE: '-',
};

const CHARGE_TYPE_OPTIONS: { value: ChargeType | ''; label: string }[] = [
  { value: '', label: 'Todos los tipos' },
  { value: ChargeType.MAINTENANCE, label: 'Mantenimiento' },
  { value: ChargeType.FINE, label: 'Multa' },
  { value: ChargeType.LATE_FEE, label: 'Recargo' },
  { value: ChargeType.EXTRA, label: 'Extraordinario' },
  { value: ChargeType.SERVICE, label: 'Servicio' },
  { value: ChargeType.DEPOSIT, label: 'Depósito' },
  { value: ChargeType.OTHER, label: 'Otro' },
];

export const AnnualBillingPage = ({ onBack }: AnnualBillingPageProps) => {
  const { report, loading, exporting, params, setYear, setChargeType, exportReport, currentYear } = useAnnualBilling();

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Tabla de Cobranza Anual</h2>
            <p className="text-slate-500 text-sm">Vista de cobranza mensual por unidad</p>
          </div>
        </div>

        <ReportExportButton onExport={exportReport} loading={exporting} disabled={loading || !report} />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Calendar size={16} />
              Año
            </label>
            <select
              value={params.year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Filter size={16} />
              Tipo de cargo
            </label>
            <select
              value={params.chargeType || ''}
              onChange={(e) => setChargeType(e.target.value as ChargeType || undefined)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {CHARGE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <Loading />
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users size={18} className="text-blue-600" />
                <span className="text-sm text-slate-600">Total Unidades</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{report.summary.totalUnits}</p>
            </div>

            <div className="bg-emerald-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={18} className="text-emerald-600" />
                <span className="text-sm text-slate-600">Recaudación</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{report.summary.annualCollectionRate.toFixed(1)}%</p>
            </div>

            <div className="bg-green-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={18} className="text-green-600" />
                <span className="text-sm text-slate-600">Al corriente</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{report.summary.unitsUpToDate}</p>
            </div>

            <div className="bg-red-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle size={18} className="text-red-600" />
                <span className="text-sm text-slate-600">Sin pago</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{report.summary.unitsNoPay}</p>
            </div>
          </div>

          {/* Totals Summary */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-sm text-slate-500">Total Esperado</div>
                <div className="text-xl font-bold text-slate-800">{formatCurrency(report.summary.grandTotalExpected)}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Total Recaudado</div>
                <div className="text-xl font-bold text-emerald-600">{formatCurrency(report.summary.grandTotalPaid)}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Saldo Pendiente</div>
                <div className="text-xl font-bold text-red-600">{formatCurrency(report.summary.grandTotalBalance)}</div>
              </div>
            </div>
          </div>

          {/* Billing Grid */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-3 font-medium text-slate-600 sticky left-0 bg-slate-50 z-10 min-w-[150px]">
                      Unidad
                    </th>
                    {report.monthTotals.map((month) => (
                      <th key={month.month} className="text-center py-3 px-2 font-medium text-slate-600 min-w-[80px]">
                        {month.monthName.substring(0, 3)}
                      </th>
                    ))}
                    <th className="text-right py-3 px-3 font-medium text-slate-600 min-w-[100px]">Total</th>
                    <th className="text-center py-3 px-3 font-medium text-slate-600 min-w-[60px]">%</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((row) => (
                    <tr key={row.unitId} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 sticky left-0 bg-white z-10">
                        <div className="font-medium text-slate-800">{row.unitName}</div>
                        <div className="text-xs text-slate-400">{row.memberName}</div>
                      </td>
                      {row.months.map((cell) => (
                        <td key={cell.month} className="py-2 px-2 text-center">
                          <span
                            className={`inline-block px-2 py-1 rounded text-xs font-medium ${STATUS_STYLES[cell.status]}`}
                            title={`Esperado: ${formatCurrency(cell.expectedAmount)} | Pagado: ${formatCurrency(cell.paidAmount)}`}
                          >
                            {cell.status === 'NO_CHARGE' ? '-' : formatCurrency(cell.paidAmount)}
                          </span>
                        </td>
                      ))}
                      <td className="py-2 px-3 text-right font-medium text-slate-800">
                        {formatCurrency(row.totalPaid)}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span className={`text-sm font-medium ${row.paymentRate >= 80 ? 'text-green-600' : row.paymentRate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {row.paymentRate.toFixed(0)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100 font-medium">
                  <tr>
                    <td className="py-3 px-3 sticky left-0 bg-slate-100 z-10 text-slate-700">
                      Totales
                    </td>
                    {report.monthTotals.map((month) => (
                      <td key={month.month} className="py-3 px-2 text-center text-slate-700">
                        {formatCurrency(month.totalPaid)}
                      </td>
                    ))}
                    <td className="py-3 px-3 text-right text-slate-800">
                      {formatCurrency(report.summary.grandTotalPaid)}
                    </td>
                    <td className="py-3 px-3 text-center text-emerald-600">
                      {report.summary.annualCollectionRate.toFixed(0)}%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-sm">
            {Object.entries(STATUS_STYLES).filter(([key]) => key !== 'NO_CHARGE').map(([status, style]) => (
              <div key={status} className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded ${style}`}>{STATUS_LABELS[status as BillingCellStatus]}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-slate-500">
          No se pudo cargar el reporte
        </div>
      )}
    </div>
  );
};
